import { DataPoint } from 'components/chart-v2/model'
import { getOptimalColor } from 'components/chart-v2/utils'

interface MultiDomainLineConfig {
  primaryDomain: string
  showLegend?: boolean
  availableMetrics?: Array<{ label: string; key: string; index: number }>
  selectedMetricIndex?: number
  allData?: DataPoint[]
}

const configureLineChart = (
  data: DataPoint[],
  config: MultiDomainLineConfig = { primaryDomain: 'volume' },
  colorMap: Record<string, string>,
): {
  type: 'line'
  data: { datasets: Record<string, unknown>[] }
  options: Record<string, unknown>
} => {
  const { availableMetrics = [], selectedMetricIndex = 0 } = config
  // Group data by brand
  const brandData = data.reduce(
    (acc, item) => {
      const brand = item.category[0] ?? ''
      const date = item.category[1] ?? ''

      if (!acc[brand]) {
        acc[brand] = []
      }

      acc[brand]!.push({
        x: date,
        y: item.values[selectedMetricIndex] ?? 0,
        ...availableMetrics.reduce(
          (metricAcc, metric, idx) => {
            metricAcc[metric.key] = item.values[idx] ?? 0
            return metricAcc
          },
          {} as Record<string, number>,
        ),
        date,
      })

      return acc
    },
    {} as Record<
      string,
      { x: string; y: number; date: string; [key: string]: string | number }[]
    >,
  )

  // Sort data points by date for each brand
  Object.keys(brandData).forEach((brand) => {
    brandData[brand]!.sort(
      (a, b) => new Date(a.x).getTime() - new Date(b.x).getTime(),
    )
  })

  const brands = Object.keys(brandData)

  const datasets = brands.map((brand, _index) => ({
    label: brand,
    data: brandData[brand],
    borderColor: getOptimalColor(brand, colorMap),
    backgroundColor: getOptimalColor(brand, colorMap) + '20', // Add transparency
    borderWidth: 2,
    fill: false,
    tension: 0.1,
    pointRadius: 4,
    pointHoverRadius: 6,
  }))

  return {
    type: 'line' as const,
    data: {
      datasets,
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'point' as const,
        intersect: false,
      },
      plugins: {
        legend: {
          display: config.showLegend !== false,
          position: 'bottom' as const,
        },
        title: {
          display: false,
          text: `${availableMetrics.find((m) => m.key === config.primaryDomain)?.label || config.primaryDomain} Over Time`,
        },
        tooltip: {
          callbacks: {
            title: function (context: Array<{ parsed: { x: number } }>) {
              const point = context[0]
              if (!point) return ''
              return new Date(point.parsed.x).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
              })
            },
            label: function (context: {
              raw: Record<string, number>
              dataset: { label?: string }
            }) {
              const point = context.raw
              const brand = context.dataset.label

              const labels = [`${brand}:`]
              availableMetrics.forEach((metric) => {
                const value = point[metric.key]
                if (value !== undefined) {
                  const formattedValue = metric.label
                    .toLowerCase()
                    .includes('volume')
                    ? value.toLocaleString()
                    : value.toFixed(2)
                  labels.push(`${metric.label}: ${formattedValue}`)
                }
              })

              return labels
            },
          },
        },
        datalabels: {
          display: false, // Disable data labels for line charts
        },
      },
      scales: {
        x: {
          type: 'time' as const,
          time: {
            unit: 'month' as const,
            displayFormats: {
              month: 'MMM yyyy',
            },
          },
          title: {
            display: true,
            text: 'Date',
          },
        },
        y: {
          beginAtZero:
            availableMetrics
              .find((m) => m.key === config.primaryDomain)
              ?.label.toLowerCase()
              .includes('volume') || false,
          max: availableMetrics
            .find((m) => m.key === config.primaryDomain)
            ?.label.toLowerCase()
            .includes('sentiment')
            ? 1
            : undefined,
          min: availableMetrics
            .find((m) => m.key === config.primaryDomain)
            ?.label.toLowerCase()
            .includes('sentiment')
            ? -1
            : undefined,
          title: {
            display: true,
            text:
              availableMetrics.find((m) => m.key === config.primaryDomain)
                ?.label || config.primaryDomain,
          },
        },
      },
    },
  }
}

export default configureLineChart
